<div class="info-container">
  @if (assetDescription() || accessLevel === 'edit') {
    <div class="description-outer">
      <div class="sidebar-section-title">Description</div>
      @if (edittingDescription()) {
        <div class="textarea-container">
          <div
            class="textarea"
            name="description"
            contenteditable
            [(wysiwyg)]="newAssetDescription"
            [allowHtml]="true"
            [maxLength]="2000"
            #descriptionInput
          ></div>
          @if (!newAssetDescription?.length) {
            <div class="placeholder">Add a description...</div>
          }
        </div>
        <div class="description-actions">
          <div class="spacer"></div>
          <div class="small-button" (click)="cancelEditDescription()">Cancel</div>
          <div class="small-button" (click)="saveDescription()">Save</div>
        </div>
      } @else if (assetDescription()) {
        <div
          class="description-container"
          [class.open]="expandedDescription()"
          [class.show-gradient]="descriptionTooBig()"
        >
          <div class="description converted-asset-description" #description [innerHTML]="assetDescription()"></div>
        </div>
        @if (descriptionTooBig() || accessLevel === 'edit') {
          <div class="description-actions">
            @if (expandedDescription()) {
              <div class="small-button" (click)="shrinkDescription()">Show less -</div>
            } @else if (descriptionTooBig()) {
              <div class="small-button" (click)="expandDescription()">Show more +</div>
            }
            <div class="spacer"></div>
            @if (accessLevel === 'edit') {
              <button
                appButton
                name="asset-desc-less"
                location="asset-info"
                color="hard-simple"
                [iconButton]="true"
                height="1"
                (click)="startEditing()"
              >
                <app-svg src="/assets/icons/edit.svg"></app-svg>
              </button>
            }
          </div>
        }
      } @else {
        <div class="add-description-button" (click)="startEditing(true)">+ Add Description</div>
      }
    </div>
  }
  <div class="sidebar-section-title metadata-title">Metadata</div>
  <div class="info-box" data-test-id="asset-creator">
    <div class="info-box-icon">
      <app-avatar [user]="creator$ | async"></app-avatar>
    </div>
    <div class="info-box-content">
      <div class="info-box-title">
        Added by
        @if (creator$ | async; as creator) {
          {{ creator.first_name + ' ' + (creator.last_name || '') }}
        } @else {
          Loading...
        }
      </div>
      <div class="info-box-desc">{{ asset.created_at * 1000 | date: 'MMM d, yyyy, hh:mm aaa (zzz)' }}</div>
    </div>
  </div>
  <div class="info-box">
    <div class="info-box-icon">
      @if (asset.type === 'image') {
        <app-svg src="/assets/icons/image.svg"></app-svg>
      }
      @if (asset.type === 'video') {
        <app-svg src="/assets/icons/video.svg"></app-svg>
      }
    </div>
    <div class="info-box-content">
      <div class="info-box-title version-container">
        <span>
          {{ asset.version_data.asset_name }}
        </span>
        @if (allowVersionHistory) {
          <app-context-menu [options]="{ width: '440px', noSpacing: true }" #menu>
            @if (asset['versions_total_count'] > 1 && ('asset-version:multiple' | billingScope)) {
              <div class="version" contextButton appClickTrack name="version" location="asset-info">
                {{ asset.version_data.version_name || 'v1' | uppercase }}
                <app-svg src="/assets/icons/12/arrow-down.svg"></app-svg>
              </div>
            }
            @if (menu.isOpen) {
              <app-asset-version-menu
                contextContent
                [asset]="asset"
                [small]="true"
                [accessLevel]="accessLevel"
                [showFooter]="true"
                (onClose)="menu.close()"
              >
              </app-asset-version-menu>
            }
          </app-context-menu>
        }
      </div>
      @if (asset.type === 'image') {
        <div class="info-box-desc">
          @if (asset.version_data.data?.width) {
            @if (asset.version_data.data.width === 16384 || asset.version_data.data.height === 16384) {
              <span
                >Over {{ asset.version_data.data.width * asset.version_data.data.height | megapixels: true }} MP</span
              >
            } @else {
              <span>{{ asset.version_data.data.width * asset.version_data.data.height | megapixels }} MP</span>
              <span>{{ asset.version_data.data.width }} × {{ asset.version_data.data.height }}</span>
            }
          }
          <span>{{ asset.version_data.size | dataUnitsBigInt: 2 }}</span>
        </div>
      }
      @if (asset.type === 'video') {
        <div class="info-box-desc">
          @if (asset.version_data.data?.width) {
            <span>
              {{ asset.version_data.data['duration'] / 60 | floor | number: '2.0-0' }}:{{
                asset.version_data.data['duration'] % 60 | number: '2.0-0'
              }}
            </span>
            <span
              >{{ asset.version_data.data.width }} × {{ asset.version_data.data.height }} ({{
                asset.version_data.data | videoResolutionType | uppercase
              }})</span
            >
          }
          @if (asset.version_data.status === 'in_progress') {
            <span>Processing...</span>
          } @else {
            <span>{{ asset.version_data.size | dataUnitsBigInt: 2 }}</span>
          }
        </div>
      }
    </div>
  </div>
  @if (asset.type === 'video') {
    <div class="info-box">
      <div class="info-box-icon">
        <app-svg src="/assets/icons/video.svg"></app-svg>
      </div>
      <div class="info-box-content">
        <div class="info-box-title version-container">
          <span>Codec Info</span>
        </div>
        <div class="info-box-desc">
          @if (asset.version_data.data?.width) {
            <span>
              {{ asset.version_data.data['video_codec'] }}
            </span>
            <span>{{ asset.version_data.data['bitrate'] / 1000 / 1000 | floor }} Mbit/s</span>
            <span>{{ convertFrameRate(asset.version_data.data['frame_rate']) }} FPS</span>
          }
        </div>
      </div>
    </div>
  }
  @if (gallery.save_camera_metadata) {
    @if (exif.camera || exif.aperture || exif.shutterSpeed || exif.iso || exif.focalLength) {
      <div class="info-box">
        <div class="info-box-icon">
          <app-svg src="/assets/icons/shutter.svg"></app-svg>
        </div>
        <div class="info-box-content">
          <div class="info-box-title">{{ exif.camera || 'Camera Info' }}</div>
          <div class="info-box-desc">
            <span>{{ exif.aperture }}</span>
            <span>{{ exif.shutterSpeed }}</span>
            <span>{{ exif.iso }}</span>
            <span>{{ exif.focalLength }}</span>
          </div>
        </div>
      </div>
    }
    @if (exif.captureTime) {
      <div class="info-box">
        <div class="info-box-icon">
          <app-svg src="/assets/icons/calendar.svg"></app-svg>
        </div>
        <div class="info-box-content">
          <div class="info-box-title">Captured on {{ exif.captureTime | date: 'MMMM dd, yyyy' }}</div>
          <div class="info-box-desc">
            {{ exif.captureTime | date: 'MMM d, yyyy, hh:mm aaa (zzz)' }}
          </div>
        </div>
      </div>
    }
  }
  @if (tenant?.name) {
    <div class="info-box">
      <div class="info-box-icon">
        <app-svg src="/assets/icons/copyright.svg"></app-svg>
      </div>
      <div class="info-box-content">
        <div class="info-box-title">{{ tenant.name }}</div>
        <div class="info-box-desc">Copyright</div>
      </div>
    </div>
  }
  @if (accessLevel === 'edit' && (growthBook.isOn('asset-tags') || (tenantTags$ | async)?.length)) {
    <div class="sidebar-section-title tags-title">Tags</div>
    <app-asset-tags [asset]="asset" [accessLevel]="accessLevel" [gallery]="gallery"></app-asset-tags>
  }
</div>
